import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Label } from '@ophelos/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@ophelos/ui/components/radio-group';
import { getAvatarFallbackClass } from '@ophelos/ui/lib/avatar-colors';
import { cn } from '@ophelos/ui/lib/utils';
import { Camera, Check } from 'lucide-react';
import { useState } from 'react';
import { ImageUploadDialog } from '@/apps/settings/pages/_partials/branding/ImageUploadDialog';
import { AVATAR_COLORS } from '@/lib/avatar-colors';
import { getInitials } from '@/lib/user-helpers';

type AvatarMode = 'image' | 'initials';

interface AvatarSectionProps {
  currentAvatarUrl: string | null;
  currentAvatarColor: string | null;
  userName: string;
  onChange: (avatarUrl: string | null, avatarColor: string | null) => void;
  renderTrigger?: (onOpen: () => void) => React.ReactNode;
  cropShape?: 'rect' | 'round';
}

function deriveMode(avatarUrl: string | null): AvatarMode {
  if (avatarUrl) return 'image';
  return 'initials';
}

export function AvatarSection({
  currentAvatarUrl,
  currentAvatarColor,
  userName,
  onChange,
  renderTrigger,
  cropShape = 'round',
}: AvatarSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Dialog-internal draft state
  const [draftMode, setDraftMode] = useState<AvatarMode>('initials');
  const [draftUrl, setDraftUrl] = useState<string | null>(null);
  const [draftColor, setDraftColor] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const handleOpen = () => {
    setDraftMode(deriveMode(currentAvatarUrl));
    setDraftUrl(currentAvatarUrl);
    setDraftColor(currentAvatarColor);
    setDialogOpen(true);
  };

  const handleModeChange = (newMode: AvatarMode) => {
    setDraftMode(newMode);
    if (newMode === 'image') {
      setDraftColor(null);
    } else if (newMode === 'initials') {
      if (!draftColor) setDraftColor('violet');
    }
  };

  const handleUploadSave = (url: string | null) => {
    if (url) setDraftUrl(url);
  };

  const handleColorSelect = (colorName: string) => {
    setDraftColor(colorName);
  };

  const handleSave = () => {
    if (draftMode === 'image') {
      onChange(draftUrl, null);
    } else {
      onChange(null, draftColor);
    }
    setDialogOpen(false);
  };

  // Dialog preview
  const previewSrc = draftMode === 'image' && draftUrl ? draftUrl : null;
  const fallbackClass = getAvatarFallbackClass(draftMode === 'initials' ? draftColor : null);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleOpen)
      ) : (
        <Button variant="outline" size="xs" onClick={handleOpen}>
          Edit
        </Button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile Picture</DialogTitle>
            <DialogDescription>Choose how your avatar appears across the app.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-4 py-4">
            <Avatar key={draftMode} className={`size-20 ${cropShape === 'rect' ? 'rounded-md' : 'rounded-full'}`}>
              {previewSrc && <AvatarImage src={previewSrc} alt={userName} />}
              <AvatarFallback className={cn('font-semibold text-3xl', fallbackClass)}>{getInitials(userName)}</AvatarFallback>
            </Avatar>
          </div>

          <RadioGroup value={draftMode} onValueChange={(v) => handleModeChange(v as AvatarMode)} className="space-y-2">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <RadioGroupItem value="image" id="avatar-image" />
                <Label htmlFor="avatar-image" className="cursor-pointer font-normal">
                  Use a photo
                </Label>
              </div>
              {draftMode === 'image' && (
                <div className="ml-7">
                  <Button variant="outline" size="sm" onClick={() => setUploadDialogOpen(true)}>
                    <Camera className="size-4 mr-2" />
                    {draftUrl ? 'Change photo' : 'Choose photo'}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <RadioGroupItem value="initials" id="avatar-initials" />
                <Label htmlFor="avatar-initials" className="cursor-pointer font-normal">
                  Use initials
                </Label>
              </div>
              {draftMode === 'initials' && (
                <div className="ml-7 flex flex-wrap gap-2">
                  {AVATAR_COLORS.map((color) => (
                    <button
                      key={color.name}
                      type="button"
                      onClick={() => handleColorSelect(color.name)}
                      className={`size-7 rounded-full transition-all flex items-center justify-center ${getAvatarFallbackClass(color.name)}`}
                      aria-label={color.label}
                      title={color.label}
                    >
                      {draftColor === color.name && <Check className="size-3.5" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </RadioGroup>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>

          <ImageUploadDialog
            open={uploadDialogOpen}
            onOpenChange={setUploadDialogOpen}
            currentUrl={draftUrl ?? undefined}
            onSave={handleUploadSave}
            accept="image/jpeg,image/png,image/webp"
            description="Upload a profile photo (JPG, PNG, or WebP)"
            cropShape={cropShape}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
